// Shared style factory used across screens and components.
// Raw colour / shadow / spacing values live in src/theme.js — use tokens there,
// not raw hex strings in JSX.

import { getTheme, getThemeCssVars } from './theme';

// ─── Design tokens ────────────────────────────────────────────────────────────
// Re-exported for any component that does: import { TOKENS } from '../styles';
export { TOKENS, THEME_OPTIONS, DEFAULT_THEME_KEY } from './theme';

export const getStyles = (isMobile, themeKey) => {
  const theme = getTheme(themeKey);
  const themeVars = getThemeCssVars(themeKey);
  const { gradients, surfaces, borders, shadows, radii, typography, zIndex } = theme;

  return {
    themeVars,
    activeTheme: theme,
    container: {
      ...themeVars,
      minHeight: '100vh',
      background: gradients.appBackground,
      padding: isMobile ? '8px' : '20px',
      fontFamily: typography.fontFamily,
      overflowX: 'hidden',
      color: theme.text.body,
    },
    card: {
      background: `linear-gradient(180deg, ${surfaces.card} 0%, ${surfaces.surface1} 100%)`,
      backdropFilter: 'blur(10px)',
      borderRadius: isMobile ? radii.lg : radii.xl,
      padding: isMobile ? '16px' : '32px',
      border: `1px solid ${borders.glass}`,
      boxShadow: `${shadows.card}, inset 0 1px 0 rgba(255,255,255,0.04)`,
    },
    input: {
      width: '100%',
      padding: isMobile ? '14px 12px' : '12px',
      borderRadius: radii.md,
      background: '#ffffff',
      border: '1px solid #e2e8f0',
      color: '#1a202c',
      fontSize: typography.fontSizeBase,
      boxShadow: shadows.input,
      outline: 'none',
      boxSizing: 'border-box',
    },
    button: {
      width: '100%',
      padding: isMobile ? '14px' : '12px',
      borderRadius: radii.md,
      background: gradients.buttonPrimary,
      color: theme.palette.white,
      border: `1px solid ${borders.active}`,
      fontSize: typography.fontSizeBase,
      fontWeight: typography.fontWeightBold,
      cursor: 'pointer',
      transition: 'all 0.2s',
      minHeight: '44px',
      boxShadow: `0 10px 22px ${theme.accents.soft}`,
    },
    greenButton: {
      background: gradients.buttonGreen,
      padding: isMobile ? '14px' : '12px',
      borderRadius: radii.md,
      color: theme.palette.white,
      border: `1px solid ${theme.accents.successBorder}`,
      fontSize: typography.fontSizeBase,
      fontWeight: typography.fontWeightBold,
      cursor: 'pointer',
      width: '100%',
      minHeight: '44px',
      boxShadow: `0 10px 22px ${theme.accents.successSoft}`,
      transition: 'all 0.2s',
    },
    compactButton: {
      background: gradients.buttonRed,
      padding: isMobile ? '10px 14px' : '8px 16px',
      borderRadius: radii.md,
      color: theme.palette.white,
      border: `1px solid ${theme.accents.dangerBorder}`,
      cursor: 'pointer',
      minHeight: '44px',
      fontWeight: typography.fontWeightBold,
      fontSize: typography.fontSizeSm,
      transition: 'all 0.15s',
      boxShadow: `0 8px 18px ${theme.accents.dangerSoft}`,
    },
    headerActionButton: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      minHeight: '44px',
      padding: isMobile ? '10px 12px' : '10px 14px',
      borderRadius: '10px',
      background: surfaces.surface1,
      border: `1px solid ${borders.default}`,
      color: theme.text.body,
      cursor: 'pointer',
      fontSize: typography.fontSizeSm,
      fontWeight: typography.fontWeightBold,
      lineHeight: 1,
      whiteSpace: 'nowrap',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
      transition: 'all 0.15s',
    },
    headerActionPill: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: '20px',
      height: '20px',
      padding: '0 6px',
      borderRadius: radii.pill,
      background: surfaces.surface2,
      border: `1px solid ${borders.default}`,
      color: theme.text.body,
      fontSize: typography.fontSizeXs,
      fontWeight: typography.fontWeightBold,
      lineHeight: 1,
      flexShrink: 0,
    },
    modalOverlay: {
      ...themeVars,
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: surfaces.overlay,
      display: 'flex',
      justifyContent: 'center',
      alignItems: isMobile ? 'flex-end' : 'center',
      zIndex: zIndex.modal,
      padding: isMobile ? '0' : '16px',
      overscrollBehavior: 'contain',
    },
    modalContent: {
      background: gradients.modal,
      borderRadius: isMobile ? `${radii.xl} ${radii.xl} 0 0` : radii.xl,
      padding: isMobile ? '20px 16px' : '32px',
      maxWidth: isMobile ? '100%' : '600px',
      width: isMobile ? '100%' : 'auto',
      maxHeight: isMobile ? '90vh' : '80vh',
      overflowY: 'auto',
      border: `2px solid ${borders.active}`,
      boxShadow: shadows.modal,
      WebkitOverflowScrolling: 'touch',
      color: theme.text.body,
    },
  };
};
